import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { brandColors } from '@config/brandTokens';
import { Link as RouterLink } from 'react-router-dom';
import type { ContestCatalogEntry } from '../../types/contestCatalog';
import {
  contestLearnGuidelinesPath,
  getSelfEvalGuidelineSnippets,
  type ContestSelfEvalConsentRecord,
  type ContestSelfEvalConsentRole,
  type ContestSelfEvalSkillConsent,
} from '@utils/contestSelfEvaluation';

export interface ContestSelfEvalConsentSkill {
  contestId: string;
  title: string;
  index?: number;
}

interface ContestSelfEvalConsentDialogProps {
  open: boolean;
  action: 'save' | 'submit' | 'video';
  sportName: string;
  ageCategory: string;
  role: ContestSelfEvalConsentRole;
  skills: ContestSelfEvalConsentSkill[];
  guidelineContest?: ContestCatalogEntry | null;
  playerName?: string;
  onCancel: () => void;
  onConfirm: (record: ContestSelfEvalConsentRecord) => void;
}

const emptySkillConsent = (): ContestSelfEvalSkillConsent => ({
  followedGuidelines: false,
  consented: false,
});

const ContestSelfEvalConsentDialog: React.FC<ContestSelfEvalConsentDialogProps> = ({
  open,
  action,
  sportName,
  ageCategory,
  role,
  skills,
  guidelineContest,
  playerName,
  onCancel,
  onConfirm,
}) => {
  const [attested, setAttested] = useState(false);
  const [skillChecks, setSkillChecks] = useState<Record<string, ContestSelfEvalSkillConsent>>({});

  const uniqueSkills = useMemo(() => {
    const seen = new Set<string>();
    return skills.filter((skill) => {
      const id = String(skill.contestId || '');
      if (!id || seen.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [skills]);
  const skillIds = uniqueSkills.map((skill) => skill.contestId).join('|');

  useEffect(() => {
    if (!open) return;
    setAttested(false);
    setSkillChecks(
      Object.fromEntries(uniqueSkills.map((skill) => [skill.contestId, emptySkillConsent()])),
    );
  }, [open, skillIds]);

  const snippets = useMemo(
    () => getSelfEvalGuidelineSnippets(guidelineContest, ageCategory),
    [guidelineContest, ageCategory],
  );

  const allSkillsReady =
    uniqueSkills.length > 0 &&
    uniqueSkills.every((skill) => {
      const row = skillChecks[skill.contestId];
      return Boolean(row?.followedGuidelines && row?.consented);
    });
  const canConfirm = attested && allSkillsReady;

  const markAllSkills = (checked: boolean) => {
    setSkillChecks(
      Object.fromEntries(
        uniqueSkills.map((skill) => [
          skill.contestId,
          { followedGuidelines: checked, consented: checked },
        ]),
      ),
    );
  };

  const updateSkill = (contestId: string, patch: Partial<ContestSelfEvalSkillConsent>) => {
    setSkillChecks((prev) => ({
      ...prev,
      [contestId]: { ...emptySkillConsent(), ...prev[contestId], ...patch },
    }));
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    onConfirm({
      role,
      attested: true,
      attestedAt: new Date().toISOString(),
      action,
      skills: skillChecks,
    });
  };

  const isParent = role === 'parent';
  const isVideo = action === 'video';
  const verb = isVideo ? 'submit' : action === 'submit' ? 'submit' : 'save';
  const pastVerb = isVideo ? 'submitted' : action === 'submit' ? 'submitted' : 'saved';
  const namedPlayer = playerName?.trim() || '';
  const playerPhrase = namedPlayer || 'the player';
  const forPlayer = namedPlayer ? ` for ${namedPlayer}` : '';
  const activityLabel = isVideo ? 'contest video' : 'self-evaluation';

  return (
    <Dialog open={open} onClose={onCancel} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 800, color: brandColors.navy, pr: 3 }}>
        {isParent
          ? `Parent / guardian consent${namedPlayer ? ` — ${namedPlayer}` : ''}`
          : isVideo
            ? `Contest video consent${namedPlayer ? ` — ${namedPlayer}` : ''}`
            : `Self-evaluation consent${namedPlayer ? ` — ${namedPlayer}` : ''}`}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={1.5}>
          <Alert severity="warning" sx={{ borderRadius: 2 }}>
            {isVideo
              ? `Contest video uses the same age-wise participation and safety guidelines as self-evaluation. Check this skill before you ${verb} for ${ageCategory}${forPlayer}.`
              : `Self-evaluation uses the same age-wise participation and safety guidelines as the contest. Check every skill you are about to ${verb} for ${ageCategory}${forPlayer}.`}
          </Alert>

          {snippets.length > 0 && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: brandColors.ice, borderRadius: 2 }}>
              {snippets.slice(0, 2).map((snippet) => (
                <Box key={snippet.key} sx={{ mb: 1.25, '&:last-child': { mb: 0 } }}>
                  <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.88rem', mb: 0.4 }}>
                    {snippet.label}
                  </Typography>
                  {snippet.content ? (
                    <Typography variant="body2" sx={{ color: brandColors.slate }}>
                      {(() => {
                        const intro = snippet.content
                          .split('\n')
                          .map((line) => line.trim())
                          .filter((line) => line && !/^[-•]/.test(line))
                          .join(' ');
                        return intro.length > 280 ? `${intro.slice(0, 280).trim()}…` : intro;
                      })()}
                    </Typography>
                  ) : null}
                  {snippet.bullets.slice(0, 4).map((bullet) => (
                    <Typography
                      key={bullet}
                      variant="body2"
                      sx={{ color: brandColors.slate, display: 'list-item', ml: 2.25, fontSize: '0.82rem' }}
                    >
                      {bullet}
                    </Typography>
                  ))}
                </Box>
              ))}
            </Paper>
          )}

          <Typography variant="body2" sx={{ color: brandColors.slate }}>
            Read the full age-wise copy on Learn —{' '}
            <strong>Online Contest Liability Disclaimer &amp; Safety Rules</strong> and{' '}
            <strong>Safety Guidelines</strong>.{' '}
            {skills[0] ? (
              <Link
                component={RouterLink}
                to={contestLearnGuidelinesPath(sportName, skills[0].contestId, ageCategory)}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ fontWeight: 700 }}
              >
                Open {ageCategory} guidelines
              </Link>
            ) : null}
            {' · '}
            <Link component={RouterLink} to="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </Link>
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={attested}
                onChange={(event) => setAttested(event.target.checked)}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {isParent
                  ? `I am the parent or legal guardian of ${playerPhrase}. I consent to this ${ageCategory} ${activityLabel} being ${pastVerb} for ${playerPhrase}, and I confirm they followed the participation and safety guidelines.`
                  : `I am completing this ${activityLabel} for myself${namedPlayer ? ` (${namedPlayer})` : ''}. I consent to ${verb} this ${ageCategory} ${activityLabel} and I followed the participation and safety guidelines.`}
              </Typography>
            }
          />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography sx={{ fontWeight: 800, color: brandColors.navy, fontSize: '0.9rem' }}>
              Skills in this {isVideo ? 'video submit' : verb} ({skills.length})
            </Typography>
            {skills.length > 1 && (
              <Button size="small" onClick={() => markAllSkills(true)} sx={{ textTransform: 'none' }}>
                Check all skills
              </Button>
            )}
          </Stack>

          {uniqueSkills.map((skill, skillIndex) => {
            const row = skillChecks[skill.contestId] || emptySkillConsent();
            const href = contestLearnGuidelinesPath(sportName, skill.contestId, ageCategory);
            const number = skill.index || skillIndex + 1;
            return (
              <Paper key={`${skill.contestId}-${number}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2 }}>
                <Stack spacing={0.25}>
                  <Typography sx={{ fontWeight: 800, color: brandColors.navy }}>
                    {number}. {skill.title}
                  </Typography>
                  <Link
                    component={RouterLink}
                    to={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ fontSize: '0.78rem', fontWeight: 700, width: 'fit-content' }}
                  >
                    {ageCategory} guidelines for this skill
                  </Link>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={row.followedGuidelines}
                        onChange={(event) =>
                          updateSkill(skill.contestId, { followedGuidelines: event.target.checked })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        Followed the {ageCategory} participation and safety guidelines for this skill
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={row.consented}
                        onChange={(event) =>
                          updateSkill(skill.contestId, { consented: event.target.checked })
                        }
                      />
                    }
                    label={
                      <Typography variant="body2">
                        {isParent
                          ? `I consent to ${verb} this skill’s ${activityLabel} for ${playerPhrase}`
                          : `I consent to ${verb} this skill’s ${activityLabel}`}
                      </Typography>
                    }
                  />
                </Stack>
              </Paper>
            );
          })}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onCancel} sx={{ textTransform: 'none' }}>
          Back to scorecard
        </Button>
        <Button
          variant="contained"
          disabled={!canConfirm}
          onClick={handleConfirm}
          sx={{ textTransform: 'none', fontWeight: 700, bgcolor: brandColors.navy }}
        >
          {isVideo
            ? `Agree and submit video${forPlayer}`
            : action === 'submit'
              ? `Agree and submit${forPlayer}`
              : `Agree and save${forPlayer}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContestSelfEvalConsentDialog;
